import api from "@/lib/axiosConfig";
import { FetchedWriting, Writing } from "@/lib/types";
import { parseJsonOrUndefined } from "@/lib/utils";
import {
  keepPreviousData,
  useMutation,
  UseMutationOptions,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import { DimensionsDTO } from "@/lib/shared-types";
import { mapFetchedDocument } from "../document/documents";
import { mapFetchedBibliography } from "../bibliography/bibliography";
import { RelativePosition } from "../dtos";
export const baseRoute = "back/api/writings";
interface WritingDTO {
  where: any;
  order: any;
  take: number;
  skip: number;
}

export const mapFetchedWriting = (fw: FetchedWriting): Writing => ({
  ...fw,
  //TECH_DEBT
  ecritsDescriptionDimensions: parseJsonOrUndefined<DimensionsDTO>(
    fw.ecritsDescriptionDimensions,
  ),
  relatedBibliography: fw.relatedBibliography?.map(mapFetchedBibliography),
  relatedDocument: fw.relatedDocument?.map(mapFetchedDocument),
});

async function getWritings({
  where,
  order,
  take,
  skip,
}: WritingDTO): Promise<Writing[]> {
  const { data } = await api.post<FetchedWriting[]>(
    `back/api/writings?take=${take || 10}&skip=${skip || 0}`,
    {
      where,
      orderBy: order,
    },
  );
  return data.map(mapFetchedWriting);
}

export const useGetAllWritings = ({ where, order, take, skip }: WritingDTO) => {
  return useQuery({
    queryKey: ["writings", where, order, take, skip],
    queryFn: () => getWritings({ where, order, take, skip }),
    placeholderData: keepPreviousData,
  });
};

export async function getCountByStatus() {
  const { data } = await api.post(`back/api/writings/countByStatus`);
  return data;
}

export const useCountByStatus = () => {
  return useQuery({
    queryKey: ["countByStatus", "writing"],
    queryFn: () => getCountByStatus(),
  });
};

export const getRelativePosition = async (
  id: string,
  order: object,
  where: object,
) => {
  const { data } = await api.post<RelativePosition>(
    `${baseRoute}/${id}/position`,
    {
      order,
      where,
    },
  );
  return data;
};

export const getWriting = async (id: string): Promise<Writing> => {
  const { data } = await api.get<FetchedWriting>(`back/api/writings/${id}`);
  return mapFetchedWriting(data);
};

export const useGetWriting = (id?: string) => {
  return useQuery({
    queryKey: ["writing", id],
    queryFn: () => (id ? getWriting(id) : undefined),
  });
};

async function updateWriting(writing: Writing) {
  const {
    instanceId: _iid,
    updatedById: _ubid,
    createdById: _cbid,
    ...rest
  } = writing;
  return await api.patch(`back/api/writings/${writing.id}`, {
    data: {
      ...rest,
      //TECH_DEBT
      ecritsDescriptionDimensions: JSON.stringify(
        rest.ecritsDescriptionDimensions,
      ),
    },
  });
}

export function useUpdateWriting() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: updateWriting,
    onSuccess: (_, v) => {
      queryClient.invalidateQueries({ queryKey: ["writing", v.id] });
      queryClient.invalidateQueries({ queryKey: ["writings"] });
    },
  });
}

type CreateWritingParams = {
  ecritsDescriptionNumeroEnregistrement: string;
  ecritsDescriptionTitre: string;
};
type CreateWritingResponse = Pick<Writing, "id">;

const createWriting = async ({
  ecritsDescriptionNumeroEnregistrement,
  ecritsDescriptionTitre,
}: CreateWritingParams) => {
  return await api
    .post<CreateWritingResponse>("back/api/writings/create", {
      ecritsDescriptionNumeroEnregistrement,
      ecritsDescriptionTitre,
    })
    .then((res) => res.data);
};

export const useCreateWriting = (
  options?: UseMutationOptions<
    CreateWritingResponse,
    Error,
    CreateWritingParams
  >,
) => {
  const queryClient = useQueryClient();

  return useMutation({
    ...options,
    mutationFn: createWriting,
    onSuccess: (data, vars, ctx) => {
      queryClient.invalidateQueries({ queryKey: ["writings"] });
      queryClient.invalidateQueries({ queryKey: ["writing", data.id] });
      queryClient.invalidateQueries({
        queryKey: ["countByStatus", "writing"],
      });
      queryClient.invalidateQueries({ queryKey: ["instance", "count-data"] });
      options?.onSuccess?.(data, vars, ctx);
    },
  });
};
